import { createApi } from '@reduxjs/toolkit/query/react';
import axiosBaseQuery from './queries';
import { Workspace, WorkspaceWithRole, User, InviteMemberRequest, InviteGuestRequest } from 'types';

interface WorkspacesResponse {
  success: boolean;
  workspaces: WorkspaceWithRole[];
}

interface WorkspaceResponse {
  success: boolean;
  workspace: WorkspaceWithRole;
}

interface MembersResponse {
  success: boolean;
  members: User[];
}

interface InviteResponse {
  success: boolean;
  message: string;
  user: User;
}

export const workspaceApi = createApi({
  reducerPath: 'workspaceApi',
  baseQuery: axiosBaseQuery({
    baseUrl: import.meta.env.VITE_APP_BASE_URL as string,
  }),
  tagTypes: ['Workspace', 'WorkspaceMember'],
  endpoints: (builder) => ({
    // Workspace CRUD
    getWorkspaces: builder.query<WorkspaceWithRole[], void>({
      query: () => ({ 
        url: '/v1/workspaces/', 
        method: 'get' 
      }),
      transformResponse: (response: WorkspacesResponse) => response.workspaces,
      providesTags: ['Workspace'],
    }),
    getWorkspace: builder.query<WorkspaceWithRole, string>({
      query: (id) => ({ 
        url: `/v1/workspaces/${id}/`, 
        method: 'get' 
      }),
      transformResponse: (response: WorkspaceResponse) => response.workspace,
      providesTags: ['Workspace'],
    }),
    createWorkspace: builder.mutation<WorkspaceWithRole, { name: string }>({
      query: (data) => ({ 
        url: '/v1/workspaces/', 
        method: 'post', 
        data 
      }),
      transformResponse: (response: WorkspaceResponse) => response.workspace,
      invalidatesTags: ['Workspace'],
    }),
    updateWorkspace: builder.mutation<WorkspaceWithRole, { id: string; data: Partial<Workspace> }>({
      query: ({ id, data }) => ({ 
        url: `/v1/workspaces/${id}/`, 
        method: 'patch', 
        data 
      }),
      transformResponse: (response: WorkspaceResponse) => response.workspace,
      invalidatesTags: ['Workspace'],
    }),

    // Workspace Members
    getWorkspaceMembers: builder.query<User[], string>({
      query: (workspaceId) => ({ 
        url: `/v1/workspaces/${workspaceId}/members/`, 
        method: 'get' 
      }),
      transformResponse: (response: MembersResponse) => response.members,
      providesTags: (result, error, workspaceId) => [
        { type: 'WorkspaceMember', id: workspaceId },
        'WorkspaceMember'
      ],
    }),
    addMember: builder.mutation<User, { workspaceId: string; data: InviteMemberRequest }>({
      query: ({ workspaceId, data }) => ({ 
        url: `/v1/workspaces/${workspaceId}/members/add/`, 
        method: 'post', 
        data 
      }),
      transformResponse: (response: InviteResponse) => response.user,
      invalidatesTags: ['WorkspaceMember'],
    }),
    inviteGuest: builder.mutation<User, { workspaceId: string; data: InviteGuestRequest }>({
      query: ({ workspaceId, data }) => ({ 
        url: `/v1/workspaces/${workspaceId}/guests/invite/`, 
        method: 'post', 
        data 
      }),
      transformResponse: (response: InviteResponse) => response.user,
      invalidatesTags: ['WorkspaceMember'],
    }),
    updateMemberRole: builder.mutation<void, { workspaceId: string; userId: string; role: string }>({
      query: ({ workspaceId, userId, role }) => ({ 
        url: `/v1/workspaces/${workspaceId}/members/${userId}/role/`, 
        method: 'patch', 
        data: { role }
      }),
      invalidatesTags: ['WorkspaceMember'],
    }),
    updateMember: builder.mutation<User, { workspaceId: string; userId: string; data: { name?: string; emailNotificationEnabled?: boolean } }>({
      query: ({ workspaceId, userId, data }) => ({ 
        url: `/v1/workspaces/${workspaceId}/members/${userId}/`, 
        method: 'patch', 
        data 
      }),
      transformResponse: (response: { success: boolean; user: User }) => response.user,
      invalidatesTags: ['WorkspaceMember'],
    }),
    removeMember: builder.mutation<void, { workspaceId: string; userId: string }>({
      query: ({ workspaceId, userId }) => ({ 
        url: `/v1/workspaces/${workspaceId}/members/${userId}/remove/`, 
        method: 'delete' 
      }),
      invalidatesTags: ['WorkspaceMember'],
    }),
  }),
});

export const {
  useGetWorkspacesQuery,
  useGetWorkspaceQuery,
  useCreateWorkspaceMutation,
  useUpdateWorkspaceMutation,
  useGetWorkspaceMembersQuery,
  useAddMemberMutation,
  useInviteGuestMutation,
  useUpdateMemberRoleMutation,
  useUpdateMemberMutation,
  useRemoveMemberMutation,
} = workspaceApi;
